import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import LocationSearch from '@/components/LocationSearch';
import { 
  Hotel, CheckCircle, ArrowRight, Phone, 
  Bed, Coffee, Utensils, Sparkles, Star
} from 'lucide-react';

const services = [
  { icon: Bed, title: 'Hotel Rooms', description: 'Luxurious guest rooms and suites' },
  { icon: Utensils, title: 'Restaurants & Bars', description: 'Dining spaces that create experiences' },
  { icon: Coffee, title: 'Lobbies & Lounges', description: 'First impressions that last' },
  { icon: Sparkles, title: 'Spa & Wellness', description: 'Tranquil relaxation zones' },
];

const benefits = [
  'Increase guest satisfaction scores',
  'Command premium room rates',
  'Create Instagram-worthy spaces',
  'Enhance operational efficiency',
  'Build lasting brand identity',
  'Stand out on booking platforms'
];

const HospitalityInterior = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    propertyName: '',
    propertyType: '',
    contactPerson: '',
    phone: '',
    email: '',
    location: '',
    rooms: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.propertyName || !formData.phone || !formData.location) {
      toast.error('Please fill in all required fields including location');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/interior-hospitality', {
        property_name: formData.propertyName,
        property_type: formData.propertyType || null,
        contact_person: formData.contactPerson || null,
        phone: formData.phone,
        email: formData.email || null,
        location: formData.location,
        num_rooms: formData.rooms ? parseInt(formData.rooms) : null,
        message: formData.message || null
      });
      
      toast.success(`Request submitted! Your Request ID: ${response.data.request_id}`);
      
      const whatsappMsg = `Hi ZenStudios! I need hospitality interior design. Request ID: ${response.data.request_id}. Property: ${formData.propertyName}`;
      window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="hospitality-interior-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Hotel className="w-4 h-4" /> Hospitality Design
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Create Memorable Guest Experiences Through Design
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                From boutique hotels to luxury resorts, we design hospitality spaces that 
                delight guests, drive bookings, and build lasting brand loyalty.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20hospitality%20interior%20design.', '_blank')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </Button>
              </div>
            </div>
            <div className="relative">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600" 
                alt="Luxury Hotel Interior"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <span className="font-semibold">5-Star Quality</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Hospitality Design Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              End-to-end interior design solutions for the hospitality industry
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-sm text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Design Drives Hospitality Success</h2>
              <p className="text-gray-600 mb-8">
                In hospitality, design is not just aesthetics—it's strategy. The right design 
                can increase booking rates by 30% and justify premium pricing.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1590490360182-c33d57733427?w=300" 
                alt="Hotel Room"
                className="rounded-xl"
              />
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=300" 
                alt="Hotel Lobby"
                className="rounded-xl mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20" id="contact-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Elevate Your Property's Design</h2>
            <p className="text-gray-600">
              Share your property details and let's create something extraordinary
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Property Name *</Label>
                  <Input
                    value={formData.propertyName}
                    onChange={(e) => setFormData({...formData, propertyName: e.target.value})}
                    placeholder="Hotel / Resort name"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Property Type</Label>
                  <Input
                    value={formData.propertyType}
                    onChange={(e) => setFormData({...formData, propertyType: e.target.value})}
                    placeholder="Hotel, Resort, Guest House..."
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="property@email.com"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Location *</Label>
                  <LocationSearch
                    value={formData.location}
                    onChange={(val) => setFormData({...formData, location: val})}
                    placeholder="Search city / destination..."
                    types="city,town,locality"
                    required
                    className="rounded-xl mt-1"
                    data-testid="hospitality-interior-location"
                  />
                </div>
                <div>
                  <Label>Number of Rooms</Label>
                  <Input
                    type="number"
                    value={formData.rooms}
                    onChange={(e) => setFormData({...formData, rooms: e.target.value})}
                    placeholder="e.g., 50"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Project Requirements</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Describe your vision, areas to design, budget range..."
                  rows={4}
                  className="rounded-xl mt-1"
                />
              </div>

              <Button type="submit" className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90" disabled={loading}>
                {loading ? 'Submitting...' : 'Request Free Consultation'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HospitalityInterior;
